import React, { useEffect, useId, useRef, useState } from 'react';
import { fetchLogos, BrandItemResponse } from "../services/logoService";
import { Props_BrandsCarouselSnippet } from "./BrandsCarousel.props";
import BannersCarouselSkeleton from "../Shared/BannersCarousel/BannersCarouselSkeleton";
import {A11y, Autoplay, Navigation, Pagination} from "swiper/modules";
import SwiperControls, {getSwiperPaginationOptions, swiperNavigationOptions} from "../Shared/SwiperControls/SwiperControls";
import {Swiper, SwiperSlide} from "swiper/react";
import { CompileHeadingToSpan } from '@ClientBase/React/ReactSnippetMDCompiler';


const SnippetBrandsCarousel = (props: Props_BrandsCarouselSnippet) => {
    const { heading, backgroundColour, settings } = props;

    const [logos, setLogos] = useState<BrandItemResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const snippetRef = useRef<HTMLElement | null>(null);
    const reactId = useId();

    const headingClass = heading.visibility.value ? 'brands-carousel__heading' : 'brands-carousel__heading brands-carousel__heading--hidden';
    const containerClass = settings.fullWidth.value ? 'brands-carousel__container brands-carousel__container--full-width' : 'brands-carousel__container';

    // Sanitize reactId to make it CSS selector-safe (React's useId returns IDs with colons like :r1:)
    const sanitizedReactId = reactId.replace(/:/g, '');
    const uniqueId = `${sanitizedReactId}`;

    useEffect(() => {
        const loadBrandLogos = async () => {
            setIsLoading(true);
            try {
                const brandData = await fetchLogos();
                setLogos(brandData);
            } catch (error) {
                console.error("Failed to load banners in component.", error);
                setLogos([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadBrandLogos();
    }, []);
    
    if (!isLoading && logos.length === 0) return null

    useEffect(() => {
        console.log('Logos state updated:', logos);
    }, [logos]);

    // Apply loaded class after content is mounted (snippetRef is null during loading state)
    useEffect(() => {
        if (!isLoading && logos.length > 0) {
            const el = snippetRef.current;
            if (el) el.classList.add('brands--loaded');
        }
    }, [isLoading, logos.length]);

    if (isLoading || logos.length === 0) {
        return <BannersCarouselSkeleton
            heading={{
                visibility: heading.visibility.value,
                content: heading.text.value ? heading.text.value : '',
            }}
            fullWidth={settings.fullWidth.value}
            bannerHeight={200}
        />;
    }

    const breakpointsSetup = {
        991: { slidesPerView: 3 },
        1200: { slidesPerView: logos.length < 4 ? logos.length : 4 },
    };

    const swiperProps = {
        key: logos.length,
        className: 'brands-carousel__swiper',
        modules: [Navigation, Pagination, A11y, Autoplay],
        autoplay: settings.autoplay.value ? {
            delay: settings.autoPlayDelay.value ? parseInt(settings.autoPlayDelay.value) : 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        } : false,
        loop: true,
        slidesPerView: 1,
        spaceBetween: 40,
        breakpoints: breakpointsSetup,
        navigation: {
            ...swiperNavigationOptions,
            prevEl: `.swiper-controls__button--prev-${uniqueId}`,
            nextEl: `.swiper-controls__button--next-${uniqueId}`,
        },
        pagination: {
            ...getSwiperPaginationOptions(),
            el: `.swiper-controls__pagination-${uniqueId}`,
        },
    };

    const swiperControlsProps = {
        uniqueId,
        showNavigationMobile: true,
        showNavigationDesktop: true,
        showPaginationMobile: true,
        showPaginationDesktop: true,
        className: 'brands-carousel__navigation',
    };

    const slides = logos.map((logo, index) => (
        <SwiperSlide key={logo.logoId || index} className="brands-carousel__slide">
            <img
                src={`${logo.logoPath}`}
                alt=""
                className="brands-carousel__image"
                loading={index === 0 ? 'eager' : 'lazy'}
            />
        </SwiperSlide>
    ));
    
    const renderSwiper = () => {
        if (logos.length === 0) return null;
        return <Swiper {...swiperProps}>{slides}</Swiper>;
    }
    
    const renderControls = () => <SwiperControls {...swiperControlsProps} />;

    const carouselContent = (
        <div>
            <div className="brands-carousel__swiper-container">
                {renderSwiper()}
            </div>
            {renderControls()}
        </div>
    );

    return (
        <div className="brands-carousel" style={{ '--background-colour': backgroundColour.hexCode?.value ? backgroundColour.hexCode.value : '#fff' } as React.CSSProperties}
        >
            <section 
                key={uniqueId} 
                ref={snippetRef} 
                className={containerClass}
            >
                <div>
                    {heading.text?.value &&
                        <h2 className={headingClass}>{CompileHeadingToSpan(heading.text.value)}</h2>
                    }
                    {carouselContent}
                </div>
            </section>
        </div>
    );
}


export default SnippetBrandsCarousel